import React, { useEffect, useRef, useState } from "react";
import "./userProfileTab.scss";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faSquareList, faUser, IconDefinition, faArrowLeftArrowRight, faCheck } from "@fortawesome/pro-regular-svg-icons";
import { useHistory } from "react-router";
import moment from "moment";
import { showError, showSuccess } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import { PostStaffAccountShiftClockIn, PutStaffAccountShiftClockOut } from "api/rpc/2024-04/facilityAdmin/client/shifts";
import { setStaffAccountShift } from "redux/actions/auth";
import { IStaffAccountShift } from "redux/reducers/models/shift";
import { GetFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import classNames from "classnames";
import ReactDOM from "react-dom";

interface IProps {
    children: React.ReactNode;
    handleSwitchUser?: () => void;
    viewProfilePath?: string;
    showShiftSummary?: boolean;
    showClockIn?: boolean;
}

interface IProfileTabAction {
    icon: IconDefinition;
    title: string;
    onClick: () => void;
}

export default function UserProfileTab(props: IProps) {
    const { children, handleSwitchUser, viewProfilePath, showShiftSummary = true, showClockIn = true } = props;
    const { t } = useTranslation();
    const [tabIsOpen, setTabIsOpen] = useState<boolean>(false);
    const [clockTime, setClockTime] = useState<string>("");
    const [clockNote, setClockNote] = useState<string>("");
    const tabRef = useRef<HTMLDivElement>(null);
    const [userRoles, setUserRoles] = useState<Array<{ id: number; title: string; wage?: number; }>>([]);
    const [selectedRole, setSelectedRole] = useState<{ id: number; title: string; wage?: number; }>(null);
    const [switchRolesOpen, setSwitchRolesOpen] = useState<boolean>(false);

    const { authStore, facilityStore } = useAppSelector(store => store);
    const dispatch = useAppDispatch();
    const history = useHistory();

    useEffect(() => {
        if (authStore?.user && showClockIn) {
            void getUserRoles();
        }
    }, [authStore?.user, showClockIn]);

    useEffect(() => {
        document.addEventListener("mousedown", handleOutsideDropdownClick);
        document.addEventListener("touchstart", handleOutsideDropdownClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideDropdownClick);
            document.removeEventListener("touchstart", handleOutsideDropdownClick);
        };
    }, []);

    useEffect(() => {
        function formatTime(timeDifference: moment.Duration) {
            const hours = timeDifference.hours().toString().padStart(2, "0");
            const minutes = timeDifference.minutes().toString().padStart(2, "0");
            const seconds = timeDifference.seconds().toString().padStart(2, "0");
            if (hours === "0" || hours === "00") {
                return `${minutes}:${seconds}`;
            } else {
                return `${hours}:${minutes}:${seconds}`;
            }
        }

        function updateClockTime() {
            const clockInTime = moment.utc((authStore?.shift?.clock_in_actual_at ?? authStore?.shift?.clock_in_at) as string)?.local();
            const currentTime = moment();
            const duration = moment.duration(currentTime.diff(clockInTime));
            const formattedElapsedTime = formatTime(duration);
            setClockTime(formattedElapsedTime);
        }

        let interval: number | null = null;

        if (tabIsOpen && authStore?.shift && authStore?.shift?.clock_in_at && !authStore?.shift?.clock_out_at) {
            //Update the clock time on tab open
            void updateClockTime();
            interval = window.setInterval(() => {
                //Update the clock time every second while the tab is open
                void updateClockTime();
            }, 1000);
        } else if (authStore?.shift && authStore?.shift?.clock_in_at && authStore?.shift?.clock_out_at) {
            setClockTime("00:00");
        }

        return () => {
            if (interval) {
                window.clearInterval(interval);
            }
        };
    }, [authStore?.shift, tabIsOpen]);

    async function getUserRoles() {
        const res = await GetFacilityAdmin({ id: authStore?.user?.id, roles: true }, false);
        if (res?.status === StatusCode.OK && res?.data && res?.data?.length > 0) {
            setUserRoles(res?.data[0]?.roles?.sort((a, b) => a.title?.localeCompare(b?.title)));
            if (res?.data[0]?.roles?.length > 0) {
                setSelectedRole(res?.data[0]?.roles[0]);
            } else {
                setSelectedRole(null);
            }
        }
    }

    function handleOutsideDropdownClick(event: any) {
        if (!tabRef.current || tabRef.current.contains(event.target as Node)) {
            return;
        }

        setSwitchRolesOpen(false);
        setTabIsOpen(false);
    }

    async function handleClockIn() {
        const clockInMeters = facilityStore?.teeSheetSettings?.clock_in_radius_meters;

        //Check location
        if (clockInMeters && clockInMeters > 0) {
            navigator.geolocation.getCurrentPosition(
                async position => {
                    const { latitude, longitude } = position.coords;
                    // Handle clock in with latitude, longitude
                    const res = await PostStaffAccountShiftClockIn({ notes: clockNote, latitude, longitude, role_id: selectedRole?.id ? selectedRole?.id : undefined }, true);
                    if (res?.status !== StatusCode.OK) {
                        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error clocking in")));
                        return;
                    }
                    dispatch(showSuccess(t("Successfully clocked in")));
                    dispatch(setStaffAccountShift(res?.data as IStaffAccountShift));
                },
                error => {
                    // Location denied
                    dispatch(showError(error.message));
                },
                {
                    enableHighAccuracy: true,
                },
            );
        } else {
            //Clock in without checking location
            const res = await PostStaffAccountShiftClockIn({ notes: clockNote, role_id: selectedRole?.id ? selectedRole?.id : undefined }, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error clocking in")));
                return;
            }
            dispatch(showSuccess(t("Successfully clocked in")));
            dispatch(setStaffAccountShift(res?.data as IStaffAccountShift));
        }
    }

    async function handleClockOut() {
        const res = await PutStaffAccountShiftClockOut({ notes: clockNote }, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error clocking out")));
            return;
        }

        if (clockNote) {
            setClockNote("");
        }
        dispatch(showSuccess(t("Successfully clocked out")));
        dispatch(setStaffAccountShift(res?.data as IStaffAccountShift));
    }

    const isStarter = Boolean(authStore?.user?.permissions?.tee_sheet_starter);

    const profilePath = viewProfilePath ?? `/admin/settings/staff/${authStore?.user?.id}`;

    const actions: Array<IProfileTabAction> = [
        ...(!isStarter ? [
            {
                icon: faUser,
                title: t("View Profile"),
                onClick: () => history.push(profilePath),
            },
            ...(showShiftSummary ? [{
                icon: faSquareList,
                title: t("Shift Summary"),
                onClick: () => history.push(profilePath, { params: { selectedTab: 2 } }),
            }] : []),
        ] : []),
        ...(handleSwitchUser ? [{
            icon: faArrowLeftArrowRight,
            title: t("Switch User"),
            onClick: () => handleSwitchUser(),
        }] : []),
    ];

    return (
        <div ref={tabRef} className="ui-user-profile-tab">
            <OverlayTrigger
                placement="auto"
                overlay={
                    tabIsOpen ? <p></p> :
                        <Tooltip id="leftMenuToolTipProfile">{`${t("Profile")}`}</Tooltip>
                }
            >
                <div onClick={() => setTabIsOpen(prev => !prev)}>{children}</div>
            </OverlayTrigger>
            <div style={{ display: tabIsOpen ? "block" : "none" }} className="ui-user-profile-tab-container">
                <div className="ui-user-profile-tab-section">
                    <div className="ui-user-profile-tab-header">
                        <div className="ui-user-profile-tab-avatar">
                            <p>{authStore?.user?.first_name?.charAt(0)}{authStore?.user?.last_name?.charAt(0)}</p>
                        </div>
                        <div>
                            <p className="ui-user-profile-tab-text-primary">{authStore?.user?.full_name}</p>
                            <p className="ui-user-profile-tab-text-secondary">{authStore?.user?.email}</p>
                        </div>
                    </div>
                </div>
                {!authStore?.user?.support_session ? (
                    <>
                        {showClockIn ? (
                            <div className="ui-user-profile-tab-section">
                                {(!authStore?.shift || (authStore?.shift?.clock_in_at && authStore?.shift?.clock_out_at)) ? (
                                    <Button type="primary" size="small" block onClick={handleClockIn}><FontAwesomeIcon icon={faClock} className="mr-1" />{t("Clock In")}</Button>
                                ) : (
                                    <Button type="primary" size="small" block onClick={handleClockOut} className="text-nowrap"><FontAwesomeIcon icon={faClock} className="mr-1" />{t("Clock Out")} ({clockTime})</Button>
                                )}
                            </div>
                        ) : null}
                        {showClockIn && userRoles?.length > 1 && (!authStore?.shift?.clock_in_at || (authStore?.shift?.clock_in_at && authStore?.shift?.clock_out_at)) ? (
                            <div className="ui-user-profile-tab-section flex justify-between items-center gap-2 relative">
                                <p className="ui-user-profile-tab-role">{t("Clock in as")} {selectedRole?.title}</p>
                                {userRoles?.length > 1 ? (
                                    <Button type="secondary" size="xsmall" onClick={() => setSwitchRolesOpen(prev => !prev)}>{t("Switch")}</Button>
                                ) : null}

                                {switchRolesOpen ? (
                                    <div className="ui-user-profile-tab-roles-popup">
                                        {userRoles?.map((role, index) => {
                                            return (
                                                <div key={index} className="ui-user-profile-tab-roles-popup-section-outer" onClick={() => {
                                                    ReactDOM.unstable_batchedUpdates(() => {
                                                        setSelectedRole(role);
                                                        setSwitchRolesOpen(false);
                                                    });
                                                }}>
                                                    <div className={classNames("ui-user-profile-tab-roles-popup-section-inner", { "ui-user-profile-tab-roles-popup-section-inner-selected": selectedRole?.id === role?.id })}>
                                                        {selectedRole?.id === role?.id ? <FontAwesomeIcon icon={faCheck} className="mr-2" /> : null} {role?.title}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <div className="ui-user-profile-tab-section-list">
                            {actions?.map((action, index) => {
                                return (
                                    <div key={index} className="ui-user-profile-tab-section-list-item" onClick={() => {
                                        setTabIsOpen(false);
                                        void action.onClick();
                                    }}>
                                        <FontAwesomeIcon icon={action.icon} />
                                        {action.title}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}